import type { TaskRunBriefEvidence, TaskRunBriefSectionEvidence } from "../../../../shared/types";
import { BRIEF_EVIDENCE_SECTIONS, evidenceCountForSection, matchBriefEvidenceSection, resolveSectionAnchor } from "./briefEvidence";

interface BriefSectionEvidenceMapProps {
  content: string;
  findings: TaskRunBriefEvidence[];
  sectionEvidence?: TaskRunBriefSectionEvidence[];
}

export function BriefSectionEvidenceMap({ content, findings, sectionEvidence }: BriefSectionEvidenceMapProps) {
  const anchorsBySectionKey = new Map<string, string>();
  const presentSectionAnchors = new Set(
    content
      .split("\n")
      .map((line) => {
        const section = matchBriefEvidenceSection(line);
        if (!section) return null;
        const anchor = resolveSectionAnchor(section, sectionEvidence, line);
        anchorsBySectionKey.set(section.key, anchor);
        return anchor;
      })
      .filter((anchor): anchor is string => anchor !== null),
  );
  const sections = BRIEF_EVIDENCE_SECTIONS.map((section) => ({
    ...section,
    anchor: anchorsBySectionKey.get(section.key) ?? resolveSectionAnchor(section, sectionEvidence),
    present: presentSectionAnchors.has(anchorsBySectionKey.get(section.key) ?? resolveSectionAnchor(section, sectionEvidence)),
    count: evidenceCountForSection(section, findings, sectionEvidence),
  })).filter((section) => section.present && section.count > 0);

  if (sections.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <h2 className="text-sm font-semibold text-slate-800">Evidence by section</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {sections.map((section) => (
          <a
            key={section.anchor}
            href={`#${section.anchor}`}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-100"
          >
            <span>{section.label}</span>
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
              {section.count}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
